import type { QueryClient } from '@tanstack/react-query';
import type { PipelineStatusResponse, StepState } from '@/types/pipeline';
import { queryKeys } from '@/config/query-client';

/**
 * Optimistically flip a lesson pipeline step to "processing" in the
 * react-query cache so retry UIs update the instant the button is clicked,
 * instead of waiting up to one poll interval (3s) for the server to reflect it.
 *
 * Every retry entry point that (a) triggers async regeneration of a pipeline
 * step and (b) renders its state from `usePipelineStatus` should call this on
 * click and invoke the returned rollback in its error handler, so a failed
 * queue request doesn't leave a fake "processing" state.
 *
 * Returns a no-op rollback when the step isn't in the cache yet (nothing to
 * flip or restore). The poller reconciles with server truth on its next tick.
 */
export function optimisticallyMarkStepProcessing(
  queryClient: QueryClient,
  lessonId: string,
  step: string
): () => void {
  const key = queryKeys.lessons.pipelineStatus(lessonId);
  const prev = queryClient.getQueryData<PipelineStatusResponse>(key);
  const stepView = prev?.steps?.[step];
  if (!prev || !stepView) return () => {};

  // Stop an in-flight poll from clobbering the optimistic value with the stale
  // "failed" state before the request resolves.
  void queryClient.cancelQueries({ queryKey: key });
  queryClient.setQueryData<PipelineStatusResponse>(key, {
    ...prev,
    steps: {
      ...prev.steps,
      [step]: { ...stepView, state: 'processing' as StepState, error: null },
    },
  });

  return () => queryClient.setQueryData(key, prev);
}

/**
 * Optimistically flip the lesson's source ingestion (the `sources` view in
 * pipeline-status) to "processing" - used by the "retry all failed sources"
 * path so panels driven by `sources.overall`/`items[].state` update on click.
 * Failed items and the overall status flip to processing; already-terminal
 * `completed` items are left alone. Returns a rollback fn.
 */
export function optimisticallyMarkSourcesProcessing(
  queryClient: QueryClient,
  lessonId: string
): () => void {
  const key = queryKeys.lessons.pipelineStatus(lessonId);
  const prev = queryClient.getQueryData<PipelineStatusResponse>(key);
  if (!prev?.sources) return () => {};

  void queryClient.cancelQueries({ queryKey: key });
  queryClient.setQueryData<PipelineStatusResponse>(key, {
    ...prev,
    sources: {
      ...prev.sources,
      overall: 'processing' as StepState,
      items: prev.sources.items.map(item =>
        item.state === 'completed'
          ? item
          : { ...item, state: 'processing' as StepState, error: null }
      ),
    },
  });

  return () => queryClient.setQueryData(key, prev);
}
